/**
 * Migros sipariş fişi yardımcıları.
 *
 * YS ve Getir için olduğu gibi (`yemeksepetiReceiptBuilder.ts`,
 * `getirReceiptBuilder.ts`), manuel modal ile otomatik fiş yazdırma
 * arasındaki tek "doğruluk kaynağı". Auto akış (`useKitchenPrintHandlers.ts`
 * Migros dalı) bu helper'ı kullanır; manuel modal şu an inline mantık
 * kullanıyor (regresyon riski nedeniyle dokunulmadı), ileride opsiyonel
 * olarak refactor edilebilir.
 *
 * KRİTİK NÜANS: Migros backend'i tutarları KURUŞ cinsinden long olarak
 * gönderir. Manuel modal `/100` ile TL'ye çevirir; bu helper aynısını
 * yapar. Auto eskiden bunu unutmuştu → fiyatlar 100 kat şişik basılıyordu.
 */

export interface MigrosReceiptSubItem {
  name: string;
}

export interface MigrosReceiptItem {
  name: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  vatRate: number;
  subItems?: MigrosReceiptSubItem[];
}

export interface MigrosReceiptTotals {
  subtotal: number;
  grandTotal: number;
  discountTotal: number;
}

/**
 * `MigrosOrderItem.options[]` listesini fiş alt-satırlarına çevirir.
 *
 * Auto akışın geçmişte oluşturduğu zengin mantık bu helper'a taşındı:
 *   - `excluded=true` opsiyonlar `headerName` bazında gruplandırılıp
 *     "CIKARILDI ({header}): {items}" formatında basılır.
 *   - `excluded=false` opsiyonlar tek tek "+ {headerName}: {itemNames}" +
 *     fiyat/qty eki ile basılır.
 *   - Fiyat (`primaryDiscountedPrice` veya `primaryPrice`) kuruş cinsinden
 *     gelir; `/100` ile TL'ye çevrilir.
 */
export function buildMigrosReceiptSubItems(
  options: any[] | undefined | null,
): MigrosReceiptSubItem[] {
  if (!options || options.length === 0) return [];
  const subs: MigrosReceiptSubItem[] = [];

  const excluded = options.filter((o: any) => o?.excluded);
  const included = options.filter((o: any) => !o?.excluded);

  if (excluded.length > 0) {
    const grouped = new Map<string, string[]>();
    for (const e of excluded) {
      const key = e.headerName || 'Malzemeler';
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(e.itemNames || '');
    }
    grouped.forEach((items, header) => {
      subs.push({ name: `CIKARILDI (${header}): ${items.join(', ')}` });
    });
  }

  for (const opt of included) {
    const priceTL = ((opt.primaryDiscountedPrice || opt.primaryPrice || 0) / 100);
    const priceStr = priceTL > 0 ? ` +${priceTL.toFixed(2)}TL` : '';
    const qtyStr = (opt.quantity || 1) > 1 ? ` x${opt.quantity}` : '';
    subs.push({ name: `${opt.headerName || ''}: ${opt.itemNames || ''}${qtyStr}${priceStr}` });
  }

  return subs;
}

/**
 * Migros sipariş ürünlerini fiş satırlarına çevirir.
 *
 * Manuel modal mantığını birebir uygular:
 *   - `unitPrice = (it.unitPrice || it.price || 0) / 100`  (kuruş→TL)
 *   - `totalPrice = (it.price || 0) / 100`  (line toplamı zaten verilir;
 *      `unitPrice * quantity` ile YENIDEN HESAPLANMAZ — Migros line.price
 *      promosyon vb. nedeniyle birim fiyat çarpımından farklı olabiliyor)
 *   - `subItems` zengin (excluded gruplama + sub-options) auto'nun
 *     mantığından korunur (manuel'in basit `+ headerName: itemNames`
 *     formatından üstün)
 *   - `item.note` varsa subItem listesine `'Not: ...'` olarak eklenir
 *     (manuel'in zenginliği — auto eskiden bu satırı kaçırıyordu)
 */
export function buildMigrosReceiptItems(order: any): MigrosReceiptItem[] {
  const rawItems: any[] = order?.items || order?.products || [];
  return rawItems.map((item: any) => {
    const lineTotalTL = (item.price || 0) / 100;
    const unitTL = (item.unitPrice || item.price || 0) / 100;
    const optionSubs = buildMigrosReceiptSubItems(item.options);
    const noteSub: MigrosReceiptSubItem[] = item.note
      ? [{ name: `Not: ${item.note}` }]
      : [];
    const subItems = [...optionSubs, ...noteSub];
    return {
      name: item.name || 'Ürün',
      quantity: item.amount || 1,
      unitPrice: unitTL,
      totalPrice: lineTotalTL,
      vatRate: 0,
      subItems: subItems.length > 0 ? subItems : undefined,
    };
  });
}

/**
 * Migros adres parçalarını birleştirir.
 *
 * Manuel modal birebir kopyası: `addressDetail`, `districtName`,
 * `townName`, `cityName` dört parça (`/`-virgülle birleştirilir).
 * Auto eskiden `cityName`'i kaçırıyordu → il bilgisi fişten kayıptı.
 */
export function buildMigrosCustomerAddress(order: any): string | undefined {
  const parts = [
    order?.addressDetail,
    order?.districtName,
    order?.townName,
    order?.cityName,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : undefined;
}

/**
 * Müşteri notunu + kurye için kritik teslim bayraklarını birleştirir.
 *
 * Manuel modal birebir kopyası:
 *   - `orderNote` (varsa)
 *   - `contactlessDelivery=true` → "Temassız Teslimat"
 *   - `ringDoorBell === false` → "Kapı zilini çalma"
 *   - `saveGreen=true` → "Çevreci Paketleme"
 *
 * Auto eskiden bayrakları yansıtmıyordu → kurye için kritik talimatlar
 * (özellikle "Kapı zilini çalma") fişe basılmıyordu.
 *
 * Çıktı `' | '` ile birleştirilir, hiçbir parça yoksa `undefined` döner.
 * Template'in beklediği `orderNote` alanına yazılmalı (PackageReceipt-
 * PrintService template `customerNote`'u render etmez).
 */
export function buildMigrosOrderNote(order: any): string | undefined {
  const parts = [
    order?.orderNote,
    order?.contactlessDelivery ? 'Temassız Teslimat' : null,
    order?.ringDoorBell === false ? 'Kapı zilini çalma' : null,
    order?.saveGreen ? 'Çevreci Paketleme' : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' | ') : undefined;
}

/**
 * Migros toplamlarını TL cinsinden hesaplar.
 *
 * Manuel modal birebir kopyası (`MigrosOrderDetailModal.tsx:411-412,435-437`):
 *   - `subtotal`      = `totalPrice / 100`                      (gross, indirim öncesi)
 *   - `grandTotal`    = `(discountedPrice || totalPrice) / 100` (net, müşterinin ödediği)
 *   - `discountTotal` = `subtotal - grandTotal` (sadece pozitif)
 *
 * Auto eskiden:
 *   - `/100` UYGULAMIYORDU → 100 kat şişik tutarlar
 *   - `grandTotal = totalPrice` (discountedPrice'i göz ardı ediyordu)
 *   - `discountTotal = d.discountTotal` (var olmayan alan → her zaman 0)
 *
 * Sonuç: indirimli siparişlerde fiş tamamen yanlış değerler veriyordu.
 */
export function calculateMigrosTotals(order: any): MigrosReceiptTotals {
  const totalTL = (order?.discountedPrice || order?.totalPrice || 0) / 100;
  const originalTL = (order?.totalPrice || 0) / 100;
  return {
    subtotal: originalTL,
    grandTotal: totalTL,
    discountTotal: originalTL > totalTL ? originalTL - totalTL : 0,
  };
}
